import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/hooks/useToast';
import {
  appointmentsService,
  type AppointmentProductCatalogueItem,
} from '@/services/appointments.service';
import {
  getPatientProductTypes,
  saveAppointmentAddons,
} from '@/services/patientProductTypes.service';
import { getErrorMessage } from '@/services/apiClient';
import type { SelectOption } from '@/types/common.types';

export interface AddProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointmentId: number;
  patientId?: number | null;
  facilityId?: number | null;
  appointmentDate?: string | null;
  onRefresh: () => void;
  currentEstimatedCost?: number;
}

interface AvailableProductType {
  productTypeId: string;
  productTypeName: string;
  availableQuantity: number;
}

interface CartItem {
  productTypeId: string;
  productTypeName: string;
  quantity: number;
  unitPrice: number;
  availableQty: number;
  inCatalogue: boolean;
}

export function AddProductsModal({
  isOpen,
  onClose,
  appointmentId,
  patientId,
  facilityId,
  appointmentDate,
  onRefresh,
  currentEstimatedCost = 0,
}: AddProductsModalProps) {
  const toast = useToast();

  const [availableTypes, setAvailableTypes] = useState<AvailableProductType[]>([]);
  const [catalogue, setCatalogue] = useState<AppointmentProductCatalogueItem[]>([]);
  const [isMember, setIsMember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [cart, setCart] = useState<CartItem[]>([]);
  const [pickerValue, setPickerValue] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);

    const promises: Promise<void>[] = [];

    if (patientId) {
      promises.push(
        getPatientProductTypes(String(patientId), appointmentDate ? { availableDate: appointmentDate } : undefined)
          .then((resp: any) => {
            const data = resp?.data ?? resp;
            const ptMap: Record<string, AvailableProductType> = {};
            const productTypes = data?.['product-types'] ?? {};
            for (const [ptId, ptData] of Object.entries(productTypes) as [string, any][]) {
              const qty = Number(ptData.availableQuantity ?? 0);
              if (qty > 0 && Number(ptData.disburseType ?? 0) !== 1) {
                ptMap[ptId] = {
                  productTypeId: ptId,
                  productTypeName: ptData.productTypeName ?? `Type ${ptId}`,
                  availableQuantity: qty,
                };
              }
            }
            const sorted = Object.values(ptMap).sort((a, b) =>
              a.productTypeName.localeCompare(b.productTypeName),
            );
            console.log('[AddProducts] Patient inventory:', sorted.length, 'types with qty>0');
            sorted.forEach((t) => console.log(`  [inv] id=${t.productTypeId} name="${t.productTypeName}" avail=${t.availableQuantity}`));
            setAvailableTypes(sorted);
          })
          .catch((err) => {
            console.error('Failed to load patient product types', err);
            setAvailableTypes([]);
          }),
      );
    }

    promises.push(
      appointmentsService
        .getAppointmentProductCatalogue(facilityId, patientId)
        .then((resp) => {
          const items = resp?.items ?? (Array.isArray(resp) ? resp : []);
          console.log('[AddProducts] Catalogue loaded:', items.length, 'items, facilityId:', facilityId);
          const zeroPrice = items.filter((i: any) => (i.unitPrice ?? 0) === 0);
          if (zeroPrice.length > 0) console.log('[AddProducts] $0 catalogue items:', zeroPrice.map((i: any) => `id=${i.productTypeId} "${i.productTypeName}"`));
          setCatalogue(items);
          setIsMember(resp?.isMember ?? false);
        })
        .catch((err) => {
          console.error('Failed to load product catalogue', err);
          setCatalogue([]);
        }),
    );

    Promise.all(promises).finally(() => setLoading(false));
  }, [isOpen, patientId, facilityId, appointmentDate]);

  useEffect(() => {
    if (!isOpen) {
      setCart([]);
      setPickerValue(null);
    }
  }, [isOpen]);

  const availMap = useMemo(() => {
    const m: Record<string, number> = {};
    for (const a of availableTypes) m[a.productTypeId] = a.availableQuantity;
    return m;
  }, [availableTypes]);

  const catMap = useMemo(() => {
    const m: Record<string, AppointmentProductCatalogueItem> = {};
    for (const c of catalogue) m[String(c.productTypeId)] = c;
    return m;
  }, [catalogue]);

  const cartIds = useMemo(() => new Set(cart.map((c) => c.productTypeId)), [cart]);

  const pickerOptions: SelectOption<string>[] = useMemo(() => {
    const fromCatalogue = catalogue
      .filter((c) => {
        if (cartIds.has(String(c.productTypeId))) return false;
        if ((c.unitPrice ?? 0) === 0 && (availMap[String(c.productTypeId)] ?? 0) <= 0) return false;
        return true;
      })
      .map((c) => ({ value: String(c.productTypeId), label: c.productTypeName }));

    const fromInventory = availableTypes
      .filter((a) => !catMap[a.productTypeId] && !cartIds.has(a.productTypeId))
      .map((a) => ({ value: a.productTypeId, label: a.productTypeName }));

    if (fromInventory.length > 0) console.log('[AddProducts] Inventory-only (NOT in catalogue):', fromInventory.map((i) => `id=${i.value} "${i.label}"`));
    return [...fromCatalogue, ...fromInventory].sort((a, b) => {
      const aAvail = availMap[a.value] ?? 0;
      const bAvail = availMap[b.value] ?? 0;
      if (aAvail > 0 && bAvail === 0) return -1;
      if (aAvail === 0 && bAvail > 0) return 1;
      return a.label.localeCompare(b.label);
    });
  }, [catalogue, availableTypes, cartIds, availMap, catMap]);

  const handleAddToCart = (productTypeId: string | null) => {
    if (!productTypeId) return;
    const cat = catMap[productTypeId];
    const invItem = availableTypes.find((a) => a.productTypeId === productTypeId);
    if (!cat && !invItem) return;
    const name = cat?.productTypeName ?? invItem?.productTypeName ?? `Type ${productTypeId}`;
    const price = cat?.unitPrice ?? 0;
    const avail = availMap[productTypeId] ?? 0;
    console.log(`[AddProducts] addToCart: id=${productTypeId} "${name}" inCatalogue=${!!cat} unitPrice=${price} availableQty=${avail}`);
    setCart((prev) => [
      ...prev,
      { productTypeId, productTypeName: name, quantity: 1, unitPrice: price, availableQty: avail, inCatalogue: !!cat },
    ]);
    setPickerValue(null);
  };

  const updateQty = (ptId: string, qty: number) => {
    setCart((prev) =>
      prev.map((c) => {
        if (c.productTypeId !== ptId) return c;
        let max = 999;
        if (!c.inCatalogue) max = Math.max(1, c.availableQty);
        return { ...c, quantity: Math.min(max, Math.max(0, qty)) };
      }),
    );
  };

  const removeFromCart = (ptId: string) => {
    setCart((prev) => prev.filter((c) => c.productTypeId !== ptId));
  };

  const totalCost = useMemo(() => {
    return cart.reduce((sum, item) => {
      const charged = Math.max(0, item.quantity - item.availableQty);
      return sum + charged * item.unitPrice;
    }, 0);
  }, [cart]);

  const validCart = useMemo(() => cart.filter((c) => c.quantity > 0), [cart]);

  const runningEstimated = currentEstimatedCost + totalCost;

  const handleSave = async () => {
    if (!patientId || validCart.length === 0) return;
    setSubmitting(true);
    console.log('[AddProducts] Starting save, items:', validCart.length, 'patientId:', patientId, 'appointmentId:', appointmentId);
    const errors: string[] = [];
    let added = 0;
    for (const item of validCart) {
      try {
        console.log('[AddProducts] Sending addon:', item.productTypeName, 'ptId:', item.productTypeId, 'qty:', item.quantity);
        const resp = await saveAppointmentAddons(String(patientId), {
          scheduledAppointmentsID: String(appointmentId),
          productTypeID: item.productTypeId,
          productQuantity: item.quantity,
        });
        console.log('[AddProducts] Addon saved OK:', item.productTypeName, resp);
        added++;
      } catch (error) {
        console.error('[AddProducts] Addon FAILED:', item.productTypeName, error);
        errors.push(`${item.productTypeName}: ${getErrorMessage(error, 'unknown error')}`);
      }
    }
    console.log('[AddProducts] Loop done. added:', added, 'errors:', errors.length);
    if (errors.length > 0) {
      toast.error(`Failed to add ${errors.length} product(s)`, errors.join('; '));
    }
    if (added > 0) {
      toast.success(`${added} product(s) added successfully.`);
    }
    onRefresh();
    onClose();
    setSubmitting(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Products"
      size="lg"
      bodyClassName="overflow-visible"
      footer={
        <div className="flex items-center justify-between w-full">
          <div className="text-sm font-semibold text-amber-700">
            Estimated: ${runningEstimated.toFixed(2)}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={submitting || validCart.length === 0}
            >
              {submitting ? 'Saving...' : `Add ${validCart.length > 0 ? validCart.length : ''} Product${validCart.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {loading ? (
          <p className="text-sm text-slate-400 py-4 text-center">Loading...</p>
        ) : (
          <>
            {/* ─── Product Picker ───────────────────────────────────────── */}
            <div>
              <Select<string>
                options={pickerOptions}
                value={pickerValue}
                onChange={(val) => handleAddToCart(val)}
                placeholder="Search and select a product to add..."
                searchable
                renderOption={(opt) => {
                  const cat = catMap[opt.value];
                  const price = cat?.unitPrice ?? 0;
                  const avail = availMap[opt.value] ?? 0;
                  return (
                    <span className="flex items-center justify-between w-full gap-2">
                      <span className="truncate">{opt.label}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        {avail > 0 && (
                          <span className="text-emerald-600 font-medium text-xs">
                            {avail} avail
                          </span>
                        )}
                        {price > 0 && (
                          <span className="text-amber-600 font-semibold text-xs">
                            ${price.toFixed(2)}
                          </span>
                        )}
                      </span>
                    </span>
                  );
                }}
                id="add-product-picker"
              />
            </div>

            {cart.length > 0 ? (
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
                {cart.map((item) => {
                  const charged = Math.max(0, item.quantity - item.availableQty);
                  const lineCost = charged * item.unitPrice;

                  return (
                    <div key={item.productTypeId} className="px-3 py-2.5">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">
                            {item.productTypeName}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-xs">
                            {item.availableQty > 0 ? (
                              <span className="text-emerald-600 font-medium">
                                {item.availableQty} available
                              </span>
                            ) : (
                              <span className="text-slate-400">0 available</span>
                            )}
                            {(item.unitPrice ?? 0) > 0 && (
                              <span className="text-slate-500">
                                ${item.unitPrice.toFixed(2)}/ea
                              </span>
                            )}
                            {isMember && (
                              <span className="inline-block rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">
                                Member
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <label className="text-xs text-slate-500">Qty:</label>
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={3}
                            className="w-16 rounded border border-slate-300 px-2 py-1 text-sm text-center focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
                            value={item.quantity === 0 ? '' : item.quantity}
                            onChange={(e) => {
                              const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                              updateQty(item.productTypeId, val === '' ? 0 : parseInt(val, 10));
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.productTypeId)}
                            className="text-slate-400 hover:text-red-500 transition-colors p-0.5"
                            aria-label="Remove"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {charged > 0 && (
                        <div className="mt-1 text-xs text-amber-600 font-medium">
                          {charged} additional{lineCost > 0 ? ` at $${lineCost.toFixed(2)}` : ''}
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="px-3 py-2.5 bg-slate-50 space-y-1">
                  {currentEstimatedCost > 0 && (
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>Current estimated</span>
                      <span className="font-medium">${currentEstimatedCost.toFixed(2)}</span>
                    </div>
                  )}
                  {totalCost > 0 && (
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>New products</span>
                      <span className="font-medium text-amber-600">+${totalCost.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1 border-t border-slate-200">
                    <span className="text-sm font-semibold text-slate-700">Estimated Total</span>
                    <span className={`text-sm font-bold ${runningEstimated > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                      ${runningEstimated.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="py-6 text-sm text-slate-400 text-center">
                Select a product above to get started.
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
